import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ShieldCheck, Award, MapPin, Heart, Zap, Users } from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import RevealText from "../components/RevealText";

const PILLARS = [
  { icon: Heart, key: "pillarLanguage" },
  { icon: Zap, key: "pillarTransparent" },
  { icon: Users, key: "pillarHuman" },
];

export default function About() {
  const { t } = useTranslation();

  return (
    <Layout>
      <Helmet>
        <title>{t("aboutPage.metaTitle")} — Velcor Energy</title>
        <meta name="description" content={t("aboutPage.metaDesc")} />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-velcor-cream pt-12 md:pt-20 pb-16 md:pb-24" data-testid="about-hero">
        <div className="max-w-[1080px] mx-auto px-5 text-center">
          <span className="inline-block text-velcor-green mb-3" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase" }}>
            {t("aboutPage.kicker")}
          </span>
          <h1 className="display text-velcor-ink leading-[1.05]" style={{ fontSize: "clamp(34px, 5.5vw, 64px)" }}>
            <RevealText text={t("aboutPage.heroPre")} />{" "}
            <RevealText text={t("aboutPage.heroAccent")} className="display-accent" delay={0.2} />
          </h1>
          <p className="mt-6 text-slate-600 max-w-[680px] mx-auto leading-relaxed" style={{ fontSize: 16 }}>
            {t("aboutPage.heroLead")}
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-white py-20" data-testid="about-mission">
        <div className="max-w-[920px] mx-auto px-5 text-center">
          <span className="inline-block text-velcor-green mb-3" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase" }}>
            {t("aboutPage.missionKicker")}
          </span>
          <h2 className="display text-velcor-ink leading-[1.1]" style={{ fontSize: "clamp(28px, 3.6vw, 44px)" }}>
            {t("aboutPage.missionTitlePre")}{" "}
            <span className="display-accent">{t("aboutPage.missionTitleAccent")}</span>
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-[680px] mx-auto" style={{ fontSize: 16 }}>
            {t("aboutPage.missionBody")}
          </p>
        </div>
      </section>

      {/* Three pillars */}
      <section className="bg-slate-50 py-20" data-testid="about-pillars">
        <div className="max-w-[1080px] mx-auto px-5">
          <h2 className="display text-velcor-ink text-center leading-[1.1] mb-12" style={{ fontSize: "clamp(26px, 3.4vw, 38px)" }}>
            {t("aboutPage.pillarsTitle")}{" "}
            <span className="display-accent">{t("aboutPage.pillarsTitleAccent")}</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white rounded-2xl p-6 border border-slate-200"
              >
                <p.icon className="w-5 h-5 text-velcor-green mb-4" strokeWidth={2} />
                <h3 className="font-black text-velcor-ink mb-2 tracking-tight" style={{ fontSize: 18 }}>
                  {t(`aboutPage.${p.key}Title`)}
                </h3>
                <p className="text-slate-500 leading-relaxed" style={{ fontSize: 14 }}>
                  {t(`aboutPage.${p.key}Body`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / regulatory */}
      <section className="bg-white py-20" data-testid="about-trust">
        <div className="max-w-[920px] mx-auto px-5">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="display text-velcor-ink leading-[1.1]" style={{ fontSize: "clamp(26px, 3vw, 36px)" }}>
                {t("aboutPage.trustTitle")}{" "}
                <span className="display-accent">{t("aboutPage.trustTitleAccent")}</span>
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed" style={{ fontSize: 15 }}>
                {t("aboutPage.trustBody")}
              </p>
            </div>
            <div className="space-y-3">
              <Badge icon={ShieldCheck} label="PUCT REP# Pending" sub={t("aboutPage.badgePuct")} />
              <Badge icon={Award} label="BBB Pendiente" sub={t("aboutPage.badgeBbb")} />
              <Badge icon={MapPin} label="Houston, TX" sub="440 Louisiana St, Ste 1200" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-velcor-cream py-20 text-center">
        <div className="max-w-[680px] mx-auto px-5">
          <h2 className="display text-velcor-ink leading-[1.1]" style={{ fontSize: "clamp(26px, 3vw, 38px)" }}>
            {t("aboutPage.ctaTitle")}{" "}
            <span className="display-accent">{t("aboutPage.ctaTitleAccent")}</span>
          </h2>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/?focus=address#calcular" className="btn-green inline-flex h-12 px-6" data-testid="about-cta-primary">{t("aboutPage.ctaPrimary")}</Link>
            <Link to="/support" className="inline-flex items-center h-12 px-6 rounded-full border-2 border-velcor-ink/15 hover:border-velcor-green hover:text-velcor-green text-velcor-ink font-extrabold text-[14px] transition-all">
              {t("aboutPage.ctaSecondary")}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Badge({ icon: Icon, label, sub }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3">
      <span className="grid place-items-center w-9 h-9 rounded-lg bg-velcor-green/10 text-velcor-green">
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <div className="font-black text-[13.5px] text-velcor-ink tracking-tight">{label}</div>
        <div className="text-[12px] text-slate-500 leading-snug">{sub}</div>
      </div>
    </div>
  );
}
